import {
  DEPOSIT_AMOUNTS,
  getDepositAmounts,
  getTokenDecimals,
  getTokenMintsForNetwork,
  type DepositTokenSymbol,
  type Network,
  type TokenDecimalsSymbol,
} from "../config/constants";
import type { IndexerAsset } from "../tokenAssets";

export const INDEXER_ASSET_SOL = "SOL";
export const INDEXER_ASSET_ARCANE = "ARCANE";

export type ResolvedNoteAsset = {
  key: string;
  tokenName: TokenDecimalsSymbol;
  unit: string;
  multiplier: bigint;
  mint: string | null;
  poolAmounts: readonly number[];
  decimals: number;
};

const TOKEN_UNITS: Record<TokenDecimalsSymbol, string> = {
  Solana: "SOL",
  Arcane: "ARCANE",
  Bitcoin: "BTC",
  Ethereum: "ETH",
  USDC: "USDC",
};

export function buildAssetsForNetwork(network: Network): ResolvedNoteAsset[] {
  const mints = getTokenMintsForNetwork(network);
  const symbols = Object.keys(DEPOSIT_AMOUNTS) as DepositTokenSymbol[];

  return symbols.flatMap((symbol) => {
    const mint = symbol === "Solana" ? null : mints[symbol as TokenDecimalsSymbol];
    if (symbol !== "Solana" && !mint) {
      return [];
    }

    const decimals = getTokenDecimals(symbol);
    return [
      {
        key: symbol.toLowerCase(),
        tokenName: symbol,
        unit: TOKEN_UNITS[symbol],
        multiplier: 10n ** BigInt(decimals),
        mint,
        poolAmounts: getDepositAmounts(symbol),
        decimals,
      },
    ];
  });
}

function poolAmountToSmallestUnits(
  poolAmount: number,
  multiplier: bigint
): bigint {
  return BigInt(Math.round(poolAmount * Number(multiplier)));
}

function assetMatchesAmount(
  asset: ResolvedNoteAsset,
  amount: bigint
): boolean {
  return asset.poolAmounts.some(
    (poolAmount) =>
      poolAmountToSmallestUnits(poolAmount, asset.multiplier) === amount
  );
}

export function resolveNoteAsset(
  network: Network,
  amountStr: string,
  mint: string | null = null,
  tokenNameHint: TokenDecimalsSymbol | null = null
): ResolvedNoteAsset {
  const assets = buildAssetsForNetwork(network);

  if (mint) {
    const byMint = assets.find((asset) => asset.mint === mint);
    if (byMint) {
      return byMint;
    }
  }

  const amount = BigInt(amountStr);

  if (tokenNameHint) {
    const hinted = assets.find((asset) => asset.tokenName === tokenNameHint);
    if (hinted && assetMatchesAmount(hinted, amount)) {
      return hinted;
    }
  }

  const matches = assets.filter((asset) => assetMatchesAmount(asset, amount));
  if (matches.length === 1) {
    return matches[0]!;
  }
  if (matches.length > 1 && tokenNameHint) {
    const hinted = matches.find((asset) => asset.tokenName === tokenNameHint);
    if (hinted) {
      return hinted;
    }
  }

  for (const asset of assets) {
    if (assetMatchesAmount(asset, amount)) {
      return asset;
    }
  }

  const solAsset = assets.find((asset) => asset.key === "solana");
  if (!solAsset) {
    throw new Error("arcane-privacy-sdk: unable to resolve note asset");
  }
  return solAsset;
}

export function getIndexerDenomination(
  amount: string,
  asset: ResolvedNoteAsset
): string {
  return String(Number(amount) / Number(asset.multiplier));
}

export function getIndexerAssetFromResolved(
  asset: ResolvedNoteAsset
): IndexerAsset {
  if (asset.key === "arcane") {
    return INDEXER_ASSET_ARCANE;
  }
  if (asset.key === "solana") {
    return INDEXER_ASSET_SOL;
  }
  if (asset.mint) {
    return { mint: asset.mint };
  }
  return INDEXER_ASSET_SOL;
}
